import { RankPill } from '@/components/leaderboard/RankPill';
import { TeamEditDialog } from '@/components/teams/TeamEditDialog';
import { TeamPointsAdjustDialog } from '@/components/teams/TeamPointsAdjustDialog';
import { FacilitatorEffectDialog } from '@/components/teams/FacilitatorEffectDialog';
import type { EventStatus } from '@/config/objects/events';
import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyTableState,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Trophy,
  Upload,
  Users,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/** Event statuses where scores are not yet meaningful, so rank is hidden. */
const PRE_LAUNCH_STATUSES: readonly EventStatus[] = ['draft', 'scheduled'];

/** Shared mono micro-label chrome for the stat column headers. */
const MICRO_LABEL =
  'font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em] text-muted-foreground';

/** Fixed-width right-aligned stat cell so header + rows line up down a column. */
const STAT_CELL = 'w-16 shrink-0 text-right';

interface Team {
  id: string;
  eventId: string;
  name: string;
  code: string;
  emoji: string | null;
  iconName: string;
  iconColor: string;
  photoUrl: string | null;
  totalScore: number;
  challengesCompleted: number;
  memberCount: number;
  allianceId?: string | null;
  alliance?: {
    id: string;
    name: string;
    color: string | null;
    iconName: string;
    iconColor: string;
  } | null;
  isStaff?: boolean;
  /** null = full facilitator access, [] = read-only, subset = scoped. */
  staffPermissions?: string[] | null;
  createdAt: string;
  updatedAt: string;
}

interface TeamsListProps {
  eventId: string;
  /** Current event status — drives whether a leaderboard rank is shown. */
  eventStatus?: EventStatus;
  onAddTeam?: () => void;
  /** Opens the CSV import modal. Slot in the header next to Add Team. */
  onImportTeams?: () => void;
  /** Triggers the CSV download via the export endpoint. */
  onExportTeams?: () => void;
}

/**
 * The event Teams roster: a dense, professional listing with per-team stat
 * columns (points, completed, members) and podium-tinted rank pills that
 * only appear once ranking is meaningful (the event has launched and at
 * least one team has scored).
 *
 * @param props - Event id + status and the header action handlers.
 */
export function TeamsList({
  eventId,
  eventStatus,
  onAddTeam,
  onImportTeams,
  onExportTeams,
}: TeamsListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);
  const [pointsAdjustTarget, setPointsAdjustTarget] = useState<Team | null>(null);
  const [editTarget, setEditTarget] = useState<Team | null>(null);
  const [effectDialogOpen, setEffectDialogOpen] = useState(false);

  const { data, isLoading } = useQuery<{
    success: boolean;
    data: Team[];
  }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams`),
    enabled: !!eventId,
    // Facilitators watch this list while players join and score — poll
    // so member counts, points, and completions move without a manual
    // refresh. Polling pauses on background tabs (react-query default).
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const deleteMutation = useMutation({
    mutationFn: async (teamId: string) => {
      return apiClient.delete(`/events/${eventId}/teams/${teamId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      toast.success('Team deleted successfully');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to delete team');
    },
  });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const teams = useMemo(() => data?.data || [], [data]);

  // Rank is noise before scores matter: hide it while the event is draft or
  // scheduled, or while every team is still on zero points. When shown, order
  // the roster by score so the pill numbers read as an actual leaderboard.
  const isPreLaunch = eventStatus ? PRE_LAUNCH_STATUSES.includes(eventStatus) : false;
  const showRank = !isPreLaunch && teams.some(team => team.totalScore > 0);
  const rankedTeams = useMemo(
    () => (showRank ? [...teams].sort((a, b) => b.totalScore - a.totalScore) : teams),
    [teams, showRank],
  );

  if (isLoading) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-4 sm:px-6 py-4 !pb-4 border-b flex flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Teams</CardTitle>
            <CardDescription>
              {teams.length} team{teams.length !== 1 ? 's' : ''} registered
            </CardDescription>
          </div>
          {/* flex-wrap: on phones the toolbar drops below the title
              instead of running off the right edge. */}
          <div className="flex flex-wrap items-center gap-2">
            {teams.length > 0 && (
              <Button
                onClick={() => setEffectDialogOpen(true)}
                variant="outline"
                size="sm"
                icon={Zap}
                aria-label="Apply an effect to teams"
              >
                Apply Effect
              </Button>
            )}
            {onExportTeams && teams.length > 0 && (
              <Button
                onClick={onExportTeams}
                variant="outline"
                size="sm"
                icon={Download}
                aria-label="Export teams as CSV"
              >
                Export
              </Button>
            )}
            {onImportTeams && (
              <Button
                onClick={onImportTeams}
                variant="outline"
                size="sm"
                icon={Upload}
                aria-label="Import teams from CSV"
              >
                Import
              </Button>
            )}
            {onAddTeam && (
              <Button onClick={onAddTeam} size="sm" icon={Plus}>
                Add Team
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {teams.length === 0 ? (
            <EmptyTableState
              icon={<Users className="h-8 w-8" />}
              title="No teams registered yet"
              description="Create teams for participants to join this event."
              action={
                onAddTeam
                  ? {
                      label: 'Add First Team',
                      onClick: onAddTeam,
                      icon: Plus,
                    }
                  : undefined
              }
            />
          ) : (
            <>
              {/* Column header — aligns to the stat cells in each row.
                  Hidden on mobile, where rows fall back to a meta line. */}
              <div className="hidden items-center gap-3 border-b bg-muted/20 px-4 py-2 sm:flex">
                {showRank && <span className="h-7 w-7 shrink-0" aria-hidden="true" />}
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
                <span className={`min-w-0 flex-1 ${MICRO_LABEL}`}>Team</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Points</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Done</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Members</span>
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
              </div>

              <ul className="divide-y">
                {rankedTeams.map((team, index) => {
                  const TeamIcon = getTeamIcon(team.iconName);
                  return (
                    <li
                      key={team.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/events/${eventId}/teams/${team.id}`)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/events/${eventId}/teams/${team.id}`);
                        }
                      }}
                      className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
                    >
                      {showRank && <RankPill rank={index + 1} />}

                      {/* Team avatar — photo overrides icon when set */}
                      {team.photoUrl ? (
                        <img
                          src={team.photoUrl}
                          alt={`${team.name} team photo`}
                          className="h-8 w-8 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: `${team.iconColor}20` }}
                        >
                          <TeamIcon
                            className="h-4 w-4"
                            style={{ color: team.iconColor || DEFAULT_TEAM_ICON_COLOR }}
                          />
                        </div>
                      )}

                      {/* Name + join code + membership badges. The whole row
                          navigates, so the name needs no separate link. */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">
                            {team.name}
                          </span>
                          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.625rem] text-muted-foreground">
                            {team.code}
                          </span>
                          {team.alliance && (
                            <span
                              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium text-white"
                              style={{ backgroundColor: team.alliance.iconColor }}
                            >
                              {team.alliance.name}
                            </span>
                          )}
                          {team.isStaff && (
                            <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-amber-900">
                              {team.staffPermissions == null
                                ? 'Staff'
                                : team.staffPermissions.length === 0
                                  ? 'Staff · read-only'
                                  : 'Staff · scoped'}
                            </span>
                          )}
                        </div>
                        {/* Mobile-only meta — the stat columns collapse below sm. */}
                        <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
                          {team.totalScore} pts · {team.challengesCompleted} done ·{' '}
                          {team.memberCount} members
                        </p>
                      </div>

                      <span
                        className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-foreground sm:block`}
                      >
                        {team.totalScore.toLocaleString()}
                      </span>
                      <span
                        className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-muted-foreground sm:block`}
                      >
                        {team.challengesCompleted}
                      </span>
                      <span
                        className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-muted-foreground sm:block`}
                      >
                        {team.memberCount}
                      </span>

                      {/* Actions — 3-dot dropdown matches event challenge rows.
                          `stopPropagation` keeps the row nav from firing when
                          the menu opens. */}
                      <div onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              aria-label={`Actions for ${team.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTarget(team)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit Team
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPointsAdjustTarget(team)}>
                              <Trophy className="mr-2 h-4 w-4" />
                              Adjust Points
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(team)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
        title="Delete Team"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      {pointsAdjustTarget && (
        <TeamPointsAdjustDialog
          eventId={eventId}
          team={pointsAdjustTarget}
          open={!!pointsAdjustTarget}
          onOpenChange={open => !open && setPointsAdjustTarget(null)}
        />
      )}

      {editTarget && (
        <TeamEditDialog
          eventId={eventId}
          team={editTarget}
          open={!!editTarget}
          onOpenChange={open => !open && setEditTarget(null)}
        />
      )}

      <FacilitatorEffectDialog
        eventId={eventId}
        teams={teams}
        open={effectDialogOpen}
        onOpenChange={setEffectDialogOpen}
      />
    </>
  );
}
